import { ref } from 'vue';
import type { Ref } from 'vue';

type ZodResponse<T> = { data: T };

type Fetcher<T> = () => Promise<ZodResponse<T>>;

type Args<T> = {
  ref: Ref<T | null>;
  fetcher: Fetcher<T>;
  invalidation: number | string;
};

type Pending = { invalidation: number | string; promise: Promise<any> };

const promiseMap = new Map<Ref, Pending | null>();
const invalidationMap = new Map<Ref, number | string>();

export default function swr<T>({ fetcher, ref: stateRef, invalidation }: Args<T>) {
  if (invalidation !== invalidationMap.get(stateRef)) stateRef.value = null;

  invalidationMap.set(stateRef, invalidation);

  const error = ref(null);
  const isValidating = ref(false);

  const pending = promiseMap.get(stateRef);
  if (!pending || pending.invalidation !== invalidation) {
    isValidating.value = true;
    const promise = fetcher()
      .then((zodResponse) => {
        // A fetch for another invalidation key may have started meanwhile — discard this stale response
        if (invalidationMap.get(stateRef) === invalidation) stateRef.value = zodResponse.data;
        return zodResponse.data;
      })
      .catch((axiosError) => {
        error.value = axiosError;
        throw axiosError;
      })
      .finally(() => {
        isValidating.value = false;
        if (promiseMap.get(stateRef)?.promise === promise) promiseMap.set(stateRef, null);
      });
    promiseMap.set(stateRef, { invalidation, promise });
  }

  return {
    ref: stateRef,
    error,
    isValidating,
    promise: (promiseMap.get(stateRef) as Pending).promise as Promise<T>,
  };
}
